import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Tag, Table, Switch, Modal } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector } from "react-redux";
import { usePutAssignActionMutation } from "../../../../redux/adapters/adapterAPI";
// COMPONENTS
import GlobalAlert2 from "../../../home/GlobalAlert2";
import ButtonSquareSave from "../../../components/buttons/ButtonSquareSave";
import TableColumnLiftType from "../../../components/table/TableColumnLiftType";
import TableNoDataBox from "../../../components/table/TableNoDataBox";
import KeysConfirmSaveTableData from "../../keys/KeysConfirmSaveTableData";
// AUTHORIZATION
import GETJwtToken from "../../../../redux/authentication/authentication";
// INTERFACES
import { RootState } from "../../../../app/store";
import { AdapterInterface } from "../../../../interfaces/Adapter.interface";
// UTILS
import { getTableRowClass } from "../../../../utils/utils";
// CUSTOM HOOKS
import useGetAllActions from "../../../../customHooks/GET_requests/useGetAllActions";

export default function ActionsTable({
  adapter,
  isLoading,
}: {
  adapter: AdapterInterface;
  isLoading: boolean;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Column } = Table;
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [assignedActions, setAssignedActions] = useState<any>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [openModalConfirmSave, setOpenModalConfirmSave] = useState<boolean>(false);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { actions, isLoadingGetAllActions, errorMessageGetAllActions, isErrorGetAllActions } =
    useGetAllActions();

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    triggerPutAssignAction,
    {
      isLoading: isLoadingPutAssignAction,
      isSuccess: isSuccessPutAssignAction,
      isError: isErrorPutAssignAction,
      error: errorPutAssignAction,
    },
  ] = usePutAssignActionMutation();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onClickSave = async () => {
    const token = await GETJwtToken();
    const BODY: any = {
      actions: [],
      actions2delete: [],
    };
    assignedActions.map((action: any) => {
      if (action.active) {
        BODY.actions.push(action.id);
      }
      return true;
    });
    triggerPutAssignAction({ token, body: BODY, id: adapter.id });
  };

  const assignPreviousActions = (onlyControlItems: any[]): any[] => {
    if (!adapter?.actionCatalogs) return onlyControlItems;
    const existingIds = new Set((adapter.actionCatalogs ?? []).map((a: any) => a.id));
    return onlyControlItems.map((item) => ({
      ...item,
      active: existingIds.has(item.id),
    }));
  };

  const buildAssignedKeys = () => {
    const onlyControlItems = actions.filter((item: any) => item.type === "CONTROL");
    setTotalRows(onlyControlItems.length);
    setAssignedActions(assignPreviousActions(onlyControlItems));
  };

  const onChangeSwitch = (newValue: boolean, record: any) => {
    const index = assignedActions.findIndex((action: any) => action.id === record.id);
    if (index >= 0) {
      const copy = structuredClone(assignedActions);
      copy[index].active = newValue;
      setAssignedActions(copy);
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (actions && actions.length > 0) {
      buildAssignedKeys();
    } else {
      setAssignedActions([]);
    }
  }, [adapter]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <div
        className="generalStyles__pageHeader"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <div>
          <div className="generalStyles__flex">
            <TableColumnLiftType liftType={adapter.liftType} />
            <Tag color="orange">{adapter.protocolType}</Tag>
            <Tag color="cyan">{adapter.name || "--"}</Tag>
            <Tag color="geekblue">ACTIONS</Tag>
          </div>
        </div>
        <div className="generalStyles__flex ">
          <div>
            <ButtonSquareSave
              onClick={() => {
                setOpenModalConfirmSave(true);
              }}
              disabledButton={isLoading}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
      <Table
        locale={{
          emptyText: (
            <>
              {isErrorGetAllActions ? (
                <TableNoDataBox
                  isError={isErrorGetAllActions}
                  errorMessage={errorMessageGetAllActions}
                  noItemsFound="No actions found"
                />
              ) : (
                <div className="generalStyles__flexCenter" style={{ height: "300px" }}>
                  No Keys assigned
                </div>
              )}
            </>
          ),
        }}
        rowClassName={(record, index) => getTableRowClass(index, theme)}
        dataSource={assignedActions}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
        rowKey={(record) => record.idMessage}
        size="small"
        className="mt-3"
        loading={isLoading || isLoadingPutAssignAction || isLoadingGetAllActions}
        footer={() => (
          <div className="generalStyles__flexEnd">
            <Tag>{totalRows} Rows</Tag>
          </div>
        )}
      >
        <Column title="cmd" dataIndex="cmd" key="cmd" render={(text) => <>{text}</>} />
        <Column
          title={t("general.type")}
          dataIndex="type"
          key="type"
          render={(text) => <>{text}</>}
        />
        <Column
          title={t("general.tooltip")}
          dataIndex="tooltip"
          key="tooltip"
          render={(text) => <>{text}</>}
        />
        <Column
          title={t("general.label")}
          dataIndex="label"
          key="label"
          render={(text) => <>{text}</>}
        />
        <Column
          title={t("general.active")}
          dataIndex="active"
          key="active"
          render={(text, record) => (
            <Switch
              onChange={(e) => {
                onChangeSwitch(e, record);
              }}
              style={{ width: "80px" }}
              checkedChildren={t("general.active")}
              unCheckedChildren={t("general.inactive")}
              checked={text}
            />
          )}
        />
      </Table>
      <GlobalAlert2
        isError={isErrorPutAssignAction}
        isSuccess={isSuccessPutAssignAction}
        requestType="PUT"
        error={errorPutAssignAction}
        name="Adapter"
      />
      <Modal
        title={t("general.confirmSave")}
        open={openModalConfirmSave}
        onOk={() => {
          onClickSave();
          setOpenModalConfirmSave(false);
        }}
        onCancel={() => {
          setOpenModalConfirmSave(false);
        }}
        width="75%"
        bodyStyle={{ overflowX: "auto" }}
      >
        <KeysConfirmSaveTableData data={assignedActions} componentType="actions" />
      </Modal>
    </div>
  );
}
